import { EmptyState, Table, TableProps, TableRowProps } from "@oysterhr/ui"
import {
  TableSelectionController,
  useTableSelectionController,
} from "~/shared/components/IntegratedTable/useTableSelectedEntries"
import { IntegratedTableControllerContext } from "./IntegratedTableControllerContext"
import { ReactElement, ReactNode, cloneElement, useMemo } from "react"
import { IntegratedTableRow } from "./IntregratedRow"
import {
  IntegratedTableBulkActionsBar,
  IntegratedTableBulkActionsBarProps,
} from "./IntegratedTableBulkActionsBar"
import type { TFunction } from "react-i18next"
import { identity } from "lodash-es"
import { Identifiable } from "./types"

export type RenderRowProps<T> = {
  dataEntry: T
  isSelectedRow: boolean
  rowIndex: number
}

type AddPayload<ToType, Payload> = ToType & {
  contextPayload: Payload & DefaultContextPayload
}

type HeaderRenderFunction<Payload> = (props: DefaultContextPayload & Payload) => ReactElement

export type TableColumnDefinition<T extends Identifiable, Payload = never> = {
  header: HeaderRenderFunction<Payload>
  body: (props: AddPayload<RenderRowProps<T>, Payload>) => ReactElement
}

type DefaultContextPayload = {
  translate: TFunction
}

export interface IntegratedTableProps<T extends Identifiable, Payload = never>
  extends Omit<TableProps, "isSelectable" | "bulkActionsBar" | "children"> {
  /**
   * The controller object enables the rendering of checkboxes to select the rows. It should also be able to calculate the total number of entries.
   * ```IntegratedTable.useSelectionController()``` is the one to use in most cases.
   */
  selectionController?: TableSelectionController
  /**
   * This field contains the array of records that need to be rendered in the table.
   */
  dataCollection: T[]
  /**
   * @description
   * This field is used to define how the actual contents of the table body and head sections.
   * The body render function is called for every record in the table with the entry object being passed
   * as an argument.
   * @example
    const pricePaidColumn: TableColumnDefinition<BenefitsEnrollment> = {
      header: ({ translate }) => (
        <IntegratedTable.Header scope="col">
          {translate("table.columns.pricePaid")}
        </IntegratedTable.Header>
      ),
      body: ({ dataEntry }) => (
        <IntegratedTable.Cell className="whitespace-nowrap">
          {dataEntry.totalPrice?.formatted || "N/A"}
        </IntegratedTable.Cell>
      ),
    }

    return <IntegratedTable columnDefinitions={[pricePaidColumn]} />

}
  */
  columnDefinitions: TableColumnDefinition<T, Payload>[]
  /**
   * contextPayload gets passed down to the column definition "body" and "header" render functions. The translation function is
   * recommended to be passed as the ```translate``` property of this object
   */
  contextPayload: Payload & Partial<DefaultContextPayload>
  /**
   * Pass a function that is used to compute the props sent to each <Table.Row /> component rendered inside the table.
   */
  rowProps?: (props: RenderRowProps<T>) => Partial<TableRowProps>
  /**
   * Render function that is used to fill the slot at the right edge of the actions bar. This is used together with selection.
   */
  renderBulkActionButtons?: IntegratedTableBulkActionsBarProps["renderBulkActionButtons"]
  emptyMessage?: ReactNode
}

/**
 *
 * @description
 * This component is meant to allow for creating tables using a different interface. Underneath it uses the DS system `Table` component.
 * Primarily it should make implementing tables with selectable rows easier. It provides both a hook to manage the state of the table
 * using the address bar and also bootstraps the usage of other components attached to the `Table.*` component.
 * @example
  const pricePaidColumn: TableColumnDefinition<EntryType> = {
    header: ({ translate }) => (
      <IntegratedTable.Header scope="col">
        {translate("table.columns.pricePaid")}
      </IntegratedTable.Header>
    ),
    body: ({ dataEntry }) => (
      <IntegratedTable.Cell className="whitespace-nowrap">
        {dataEntry.totalPrice?.formatted || "N/A"}
      </IntegratedTable.Cell>
    ),
  }

  const {data, loading} = useSomeQuery();
  const entries = useMemo(() => data?.entriesQuery.collection || [], [data])
  const selectionController = IntegratedTable.useSelectionController({
    collectionSize: entries.length,
    metadata: data?.entriesQuery.metadata,
  })

  const t = useTranslation("some_translation_file")

  return <IntegratedTable
    columnDefinitions={[pricePaidColumn]}
    selectionController={selectionController} // this prop is necessary for having selectable rows
    dataCollection={entries}
    isLoading={loading}
    rowProps={({ dataEntry }) => ({
      className: "hover:bg-gray-100 cursor-pointer", // these props get computed and attached to each row in the table body
    })}
    renderBulkActionButtons={args => ( // in case you have selectable rows, you probably want to be able to run some actions
      <ButtonDropdown
        disabled={!args.selectedEntriesCount}
        dropdownOptions={[
          {
            onClick: () => console.log("Do somethings"),
            title: "My action",
            key: "Whatever",
          },
        ]}
      >
        Actions
      </ButtonDropdown>
    )}
  />
 */
export function IntegratedTable<T extends Identifiable, Payload = undefined>({
  selectionController,
  columnDefinitions,
  rowProps,
  dataCollection,
  renderBulkActionButtons,
  afterTable,
  emptyMessage,
  contextPayload,
  ...rest
}: IntegratedTableProps<T, Payload>) {
  const isSelectable = !!selectionController
  const renderedEmptyState = useMemo(
    () => <EmptyState>{emptyMessage ?? "No records found"}</EmptyState>,
    [emptyMessage],
  )

  const table = (
    <Table
      onChangeMultiSelectionState={selectionController?.onChangeMultiSelectionState}
      multiSelectionState={selectionController?.headerSelectionState}
      isSelectable={isSelectable}
      bulkActionsBar={
        !renderBulkActionButtons || !isSelectable ? undefined : (
          <IntegratedTableBulkActionsBar renderBulkActionButtons={renderBulkActionButtons} />
        )
      }
      afterTable={
        <>
          {dataCollection.length === 0 && !rest.isLoading && renderedEmptyState}
          {afterTable}
        </>
      }
      {...rest}
    >
      <Table.Head>
        <Table.Row>
          {columnDefinitions.map(({ header }, index) => {
            const renderedHeader = header({
              ...contextPayload,
              translate: contextPayload?.translate ?? identity,
            })
            if (renderedHeader.type !== Table.Header)
              throw new Error(
                "Only IntegratedTable.Header or Table.Header can be used for 'header' column definition.",
              )

            return cloneElement(renderedHeader, { key: renderedHeader.key ?? index })
          })}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {dataCollection.map((dataEntry, rowIndex) => {
          const isSelectedRow = selectionController?.isSelected(dataEntry) ?? false

          return (
            <IntegratedTableRow
              data-testid={dataEntry.id}
              {...rowProps?.({
                rowIndex,
                isSelectedRow,
                dataEntry,
              })}
              dataEntry={dataEntry}
              key={dataEntry.id}
            >
              {columnDefinitions.map(({ body }, columnIndex) => {
                const renderedCell = body({
                  dataEntry,
                  rowIndex,
                  isSelectedRow,
                  contextPayload: {
                    ...contextPayload,
                    translate: contextPayload.translate ?? identity,
                  },
                })
                if (renderedCell.type !== Table.Cell)
                  throw new Error(
                    "Only IntegratedTable.Cell or Table.Cell can be used for 'body' column definition",
                  )
                return cloneElement(renderedCell, { key: renderedCell.key ?? columnIndex })
              })}
            </IntegratedTableRow>
          )
        })}
      </Table.Body>
    </Table>
  )

  if (selectionController) {
    return (
      <IntegratedTableControllerContext.Provider value={selectionController}>
        {table}
      </IntegratedTableControllerContext.Provider>
    )
  }

  return table
}

IntegratedTable.Cell = Table.Cell
IntegratedTable.Header = Table.Header
IntegratedTable.useSelectionController = useTableSelectionController
